import type {BlankLine, RootContent} from 'mdast'
import type {Extension, Transform} from 'mdast-util-from-markdown'

import {calculateEndPoint} from '../../syntax-tree/node-range-utils'
import {isPositionedNode, type PointWithOffset, type PositionedNode} from '../../syntax-tree/positioned-node'

declare module 'mdast' {
  /**
   * A BlankLine is a collapsed node representing a completely blank line. Note that this node will only be generated
   * for blank lines at the document root - it will not be created for blank lines in blockquotes, code blocks, etc.
   */
  export interface BlankLine extends Node, DelimiterParent {
    // BlankLine will never contain children so it technically shouldn't have to extend `DelimiterParent`, but it's
    // much easier to work with the tree if we assume that _every_ node can accept delimiters as children. It saves a
    // ton of cumbersome type checks like `"children" in node` in other other extensions/utils.
    type: 'blankLine'
  }

  export interface RootContentMap {
    blankLine: BlankLine
  }
}

function blankLine(position: PointWithOffset): BlankLine & PositionedNode {
  return {
    type: 'blankLine',
    position: {
      start: position,
      end: position,
    },
    data: {
      hProperties: {
        dataBlankLine: true,
      },
    },
  }
}

export function mdastBlankLines(source: string): Extension {
  const transform: Transform = tree => {
    const childrenWithBlankLines: RootContent[] = []
    let lastNodeEnd = {column: 0, line: 0, offset: -1}

    function pushNode(node: RootContent & PositionedNode) {
      childrenWithBlankLines.push(node)
      lastNodeEnd = node.position.end
    }

    for (const rootChild of tree.children) {
      if (isPositionedNode(rootChild)) {
        // If a node starts more than 1 char (allowing for a single newline) after previous node, there is a blank line
        while (lastNodeEnd.offset + 1 < rootChild.position.start.offset) {
          pushNode(blankLine(calculateEndPoint(lastNodeEnd, '\n')))
        }

        pushNode(rootChild)
      }
    }

    // Previous loop added blank lines before and between nodes, but not after the last node:
    while (lastNodeEnd.offset + 1 < source.length) {
      pushNode(blankLine(calculateEndPoint(lastNodeEnd, '\n')))
    }

    tree.children = childrenWithBlankLines
  }

  return {transforms: [transform]}
}
